import { defineStore } from "pinia";
import { PrintingTicket } from '../other/DataStructs'
import { ref } from "vue";
import axios from "axios";
import { useAuthStore } from "./authStore";
import { useManageStore } from "./manage";

export const useTicketsStore = defineStore("ticketStore", {
    state: () => {
        return {
            //tickets
            models: ref([] as PrintingTicket[]),
            colors: [] as any,
            states: [] as any,
            priorities: [] as any,

            stateColors: ["statusNot", "statusProgress", "statusPrinted"],

            uploadSuccess: true,
            renderedCanvasImage: "",

            editFileInput: ref(),
            currentSTLFile: "",
        };
    },
    actions: {
        /**
         * Sends a request to the server to posts the given Ticket.
         * @param newTicket the Ticket which should be send.
         */
        postTicket(newTicket: PrintingTicket) {
            const formData = new FormData();
            formData.append('stl_file', this.currentSTLFile);
            formData.append('jpeg_file', this.createJPEG(this.renderedCanvasImage), newTicket.ticket_name + ".jpeg");
            formData.append('client_name', newTicket.client);
            formData.append('asignee', newTicket.assignee);
            formData.append('printer_id', newTicket.printer);
            formData.append('ticket_name', newTicket.ticket_name);
            formData.append('image_string', "");//TODO to be deleted
            formData.append('delivery_date', newTicket.deliverydate);
            formData.append('priority_id', newTicket.priority.toString());
            formData.append('status_id', newTicket.status.toString());
            formData.append('color_id', newTicket.color_id.toString());
            formData.append('comment', newTicket.comment);

            axios.post(`
                ${process.env.VUE_APP_API_URL}/database/tickets/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    }
                }
            )
                .then((response) => {
                    console.log(response);
                    //updating ui
                    this.fetchAllTickets();
                }, (error) => {
                    console.log(error);
                    this.uploadSuccess = false;
                });
        },
        /**
         * Converts a base64 imageURL string to a binary blob jpeg object.
         * @param canvasImage base64 imageURL string which should be converted to a binary blob object
         * @returns A blob object which contains the binary data for the jpeg file.
         */
        createJPEG(canvasImage: string) {
            let blob: any = "";
            // atob to base64_decode the data-URI
            const image_data = atob(canvasImage.split(',')[1]);
            // Use typed arrays to convert the binary data to a Blob
            const arraybuffer = new ArrayBuffer(image_data.length);
            const view = new Uint8Array(arraybuffer);
            for (let i = 0; i < image_data.length; i++) {
                view[i] = image_data.charCodeAt(i) & 0xff;
            }
            try {
                // This is the recommended method:
                blob = new Blob([arraybuffer], { type: 'img/jpeg' });
            }
            catch (e) {
                console.log(e);
            }
            return blob;
        },
        /**
         * Sends a request to the server to change the given ticket object with the given parameters.
         * @param ticket The ticket which should be changed.
         */
        putTicket(ticket: PrintingTicket) {
            // get index of Ticket through id.
            let index = 0;
            for (let i = 0; i < this.models.length; i++) {
                if (this.models[i].id === ticket.id) {
                    index = i;
                    break;
                }
            }

            axios.put(
                `${process.env.VUE_APP_API_URL}/database/tickets/${ticket.id}`,
                {
                    id: ticket.id,
                    client_name: ticket.client,
                    printer_id: ticket.printer,
                    asignee: ticket.assignee != undefined ? ticket.assignee : "",
                    ticket_name: ticket.ticket_name,
                    image_string: ticket.fileImageAddress,
                    model_filepath: ticket.model_filepath,
                    delivery_date: ticket.deliverydate,
                    priority_id: ticket.priority,
                    status_id: ticket.status,
                    color_id: ticket.color_id,
                    comment: ticket.comment
                },
                {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    }
                })
                .then((response) => {
                    console.log(response);
                    //updating UI
                    this.fetchAllTickets();
                }, (error) => {
                    console.log(error);
                    this.uploadSuccess = false;
                });
        },
        /**
         * Sends a request to the server that the given ticket with the id should be removed.
         * @param ticket 
         */
        removeTicket(ticket: PrintingTicket) {
            //get index of the Ticket.
            let index = 0;
            for (let i = 0; i < this.models.length; i++) {
                if (this.models[i].id == ticket.id) {
                    index = i;
                    break;
                }
            }

            axios.delete(
                `${process.env.VUE_APP_API_URL}/database/tickets/${ticket.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    }
                }
            )
                .then((response) => {
                    console.log(response);
                    //updating ui
                    this.fetchAllTickets();
                }, (error) => {
                    console.log(error);
                });

        },
        /**
         * Fetches all tickets from the server and loads them in models.
         */
        async fetchAllTickets() {
            const manageStore = useManageStore();
            let page = 1;
            const items_per_page = 10;
            //reset ui
            this.models.length = 0;
            let fetchedTickets: PrintingTicket[] = [];
            let pageLength = 10;

            //fetch next pages if first is full
            while (pageLength >= items_per_page) {
                fetchedTickets = [];
                const responseObject = await axios.get(
                    `${process.env.VUE_APP_API_URL}/database/tickets/?page=${page}&items_per_page=${items_per_page}&filtered_status=${manageStore.selectedFilter}`, {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    }
                });
                fetchedTickets = this.formatTicket(responseObject.data.items);

                //update models array (ui)
                for (const f of fetchedTickets) {
                    this.models.push(f);
                }
                pageLength = fetchedTickets.length;
                page++;
            }
        },
        async fetchTicketImage(id: string) {
            const response = await axios.get(
                `${process.env.VUE_APP_API_URL}/database/files/${id}/jpeg`,
                {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    },
                    responseType: 'blob'
                }
            )
            const blob = response.data
            const imageSrc = await this.blobToDataURL(blob);
            return imageSrc;
        },
        async blobToDataURL(blob:any) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob); // Read the Blob as a Data URL
            });
        },
        formatTicket(tickets: any): PrintingTicket[] {
            const formattedTicket: PrintingTicket[] = [];
            for (let i = 0; i < tickets.length; i++) {
                const ticket = new PrintingTicket(
                    tickets[i].client_name,
                    tickets[i].asignee,
                    tickets[i].printer,
                    tickets[i].ticket_name,
                    tickets[i].priority_id,
                    tickets[i].delivery_date,
                    tickets[i].color_id,
                    tickets[i].image_string,
                    tickets[i].model_filepath,
                    tickets[i].comment
                );
                ticket.id = tickets[i].id;
                ticket.status = tickets[i].status_id;
                formattedTicket.push(ticket);
            }
            return formattedTicket;
        },
        async fetchAllPropertyTables() {
            //fetching property tables
            const responseStatus = await axios.get(
                `${process.env.VUE_APP_API_URL}/database/status/`,
                {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    }
                }
            )
            this.states = responseStatus.data.items;

            const responseColors = await axios.get(`${process.env.VUE_APP_API_URL}/database/colors/`,
                {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    }
                }
            );
            this.colors = responseColors.data.items;

            const responsePriorities = await axios.get(`${process.env.VUE_APP_API_URL}/database/priorities/`,
                {
                    headers: {
                        Authorization: `Bearer ${useAuthStore().sessiontoken}`
                    }
                }
            )
            this.priorities = responsePriorities.data.items;
        },
        getColorFromId(id: number) {
            for (const color_obj of this.colors) {
                if (color_obj.id == id) {
                    return color_obj.name;
                }
            }
            return null;
        },
        getStateName(ticket: PrintingTicket) {
            const state = this.states.find((state: any) => state.id == ticket.status);
            if (state != undefined) {
                return state.name;
            }
            else {
                return "";
            }
        },
        /**
         * Sends a request to the server to download the STL from the given Ticket.
         * @param ticket The ticket with the STL file which should be downloaded.
         */
        /**
         * Sends a request to the server to download the STL from the given Ticket.
         * @param ticket The ticket with the STL file which should be downloaded.
         */
        async downloadSTL(ticket: PrintingTicket) {
            fetch(`${process.env.VUE_APP_API_URL}/database/files/${ticket.id}/stl`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${useAuthStore().sessiontoken}`
                }
            })
            .then(response => {
                if (response.ok) {
                    return response.blob();
                }
                throw new Error('Network response was not ok.');
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement('a');
                
                a.href = url;
                a.download = `${ticket.ticket_name}.stl`;

                document.body.appendChild(a);
                a.click();
                
                setTimeout(() => {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url); 
                }, 0);
            })
            .catch(error => {
                console.error('There has been a problem with your fetch operation:', error);
            });
        },
        /**
         * Gets the ticket from its id.
         * @param searchId id of the ticket.
         * @returns The ticket object.
         */
        async getTicket(searchId: string) {
            try {
                const response = await fetch(`${process.env.VUE_APP_API_URL}/database/tickets/${searchId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${useAuthStore().sessiontoken}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok.');
                }

                const data = await response.json();
                return data;

            } catch (error) {
                console.error('Error fetching ticket:', error);
                return undefined;
            }
        },
        /**
         * Gets the color used for ui, that should be displayed for the material.
         * @param material The material which should be displayed.
         * @returns color hex code.
         */
    },

});